import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../css/AssignedCity.css";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";

const AssignedCity = () => {
  const navigate = useNavigate();
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [cities, setCities] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedCities, setSelectedCities] = useState([]);
  const [assignedCities, setAssignedCities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    axios.get(`${BASE_URL}/api/states`, {
      headers: getAuthHeaders()
    })
      .then((res) => setStates(res.data))
      .catch((err) => console.error("Error fetching states:", err));
  }, []);

  useEffect(() => {
    if (selectedState) {
      const stateObj = states.find(state => state.name === selectedState);
      if (stateObj) {
        axios.get(`${BASE_URL}/api/cities-with-state/${stateObj.id}`, {
          headers: getAuthHeaders()
        })
          .then((res) => setCities(res.data))
          .catch((err) => console.error("Error fetching cities:", err));

        axios.get(`${BASE_URL}/api/employees?state_id=${stateObj.id}`, {
          headers: getAuthHeaders()
        })
          .then((res) => {
            const workingEmployees = res.data.filter(emp => emp.status === "WORKING");
            setEmployees(workingEmployees);
          })
          .catch((err) => console.error("Error fetching employees:", err));
      }
    }
  }, [selectedState, states]);

  useEffect(() => {
    if (selectedEmployee) {
      setLoading(true);
      axios.get(`${BASE_URL}/api/assigned-cities/${selectedEmployee}`, {
        headers: getAuthHeaders()
      })
        .then((res) => {
          setAssignedCities(res.data);
          // Auto-check assigned cities
          const assignedIds = res.data.map(city => city.city_id);
          setSelectedCities(assignedIds);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching assigned cities:", err);
          setLoading(false);
        });
    }
  }, [selectedEmployee]);

  const handleCitySelection = (cityId) => {
    setSelectedCities(prev => {
      if (prev.includes(cityId)) {
        return prev.filter(id => id !== cityId);
      } else {
        return [...prev, cityId];
      }
    });
  };

  const handleAssignCities = () => {
    if (!selectedEmployee || selectedCities.length === 0) {
      alert("Please select an employee and at least one city");
      return;
    }

    setLoading(true);
    axios.post(`${BASE_URL}/api/assign-cities/${selectedEmployee}`,
      { city_ids: selectedCities },
      { headers: getAuthHeaders() }
    )
      .then(() => {
        alert("Cities assigned successfully!");
        return axios.get(`${BASE_URL}/api/assigned-cities/${selectedEmployee}`, {
          headers: getAuthHeaders()
        });
      })
      .then((res) => {
        setAssignedCities(res.data);
        const assignedIds = res.data.map(city => city.city_id);
        setSelectedCities(assignedIds);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error assigning cities:", err);
        setLoading(false);
      });
  };

  const handleCityUnassign = (city) => {
    if (window.confirm(`Are you sure you want to remove ${city.city_name}?`)) {
      setLoading(true);
      const updatedCityIds = assignedCities
        .filter(c => c.city_id !== city.city_id)
        .map(c => c.city_id);

      axios.post(`${BASE_URL}/api/assign-cities/${selectedEmployee}`,
        { city_ids: updatedCityIds },
        { headers: getAuthHeaders() }
      )
        .then(() => {
          setAssignedCities(prev => prev.filter(c => c.city_id !== city.city_id));
          setSelectedCities(updatedCityIds);
          setLoading(false);
        })
        .catch(err => {
          console.error("Error removing city:", err);
          setLoading(false);
        });
    }
  };

  const filteredEmployees = employees.filter(employee => {
    const searchString = `${employee.first_name} ${employee.last_name} ${employee.designation} ${employee.headquarter_name} ${employee.division}`.toLowerCase();
    return searchString.includes(searchTerm.toLowerCase());
  });

  const formatEmployeeDisplay = (employee) => {
    return `${employee.first_name} ${employee.last_name} (${employee.designation} | ${employee.headquarter_name} | ${employee.division})`;
  };

  return (
    <div className="master-container">
      <div className="ed-city-containeassignr">
        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 mb-4">
  <span className="text-blue-700">ASSIGN </span>
  <span className="text-blue-500">CITIES</span>
</h1>

        <div className="filter-section">
        
          <div className="filter-row">
            <label className="filter-label">Select State:</label>
            <select
              className="filter-select"
              value={selectedState}
              onChange={(e) => {
                setSelectedState(e.target.value);
                setSelectedEmployee("");
                setSelectedCities([]);
                setAssignedCities([]);
              }}
            >
              <option value="">-- Select State --</option>
              {states.map(state => (
                <option key={state.id} value={state.name}>{state.name}</option>
              ))}
            </select>
          </div>

          {selectedState && (
            <div className="filter-row">
              <label className="filter-label">Select Employee:</label>
              <select
                className="filter-select"
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
              >
                <option value="">-- Select Employee --</option>
                {filteredEmployees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {formatEmployeeDisplay(emp)}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {selectedState && (
          <div className="city-selection-section">
            <h3 className="sub-title">Available Cities in {selectedState}</h3>
            <div className="city-grid">
              {cities.map(city => (
                <div key={city.id} className="city-item">
                  <input
                    type="checkbox"
                    id={`city-${city.id}`}
                    checked={selectedCities.includes(city.id)}
                    onChange={() => handleCitySelection(city.id)}
                  />
                  <label htmlFor={`city-${city.id}`}>
                    {city.name}
                    {assignedCities.some(ac => ac.city_id === city.id) && (
                      <span className="assigned-badge">(Assigned)</span>
                    )}
                  </label>
                </div>
              ))}
            </div>
            <button
              className="assign-btn"
              onClick={handleAssignCities}
              disabled={loading || selectedCities.length === 0}
            >
              {loading ? "Processing..." : "Assign Selected Cities"}
            </button>
          </div>
        )}

        {selectedEmployee && (
          <div className="assigned-cities-section">
        <h3 className="text-lg sm:text-xl font-bold text-center text-gray-800 mb-4">
  <span className="text-[rgb(3,105,161)]">CURRENTLY </span>
  <span className="text-[rgb(37,99,235)]">ASSIGNED </span>
  <span className="text-[rgb(59,130,246)]">CITIES</span>
</h3>

  
            {loading ? (
              <p>Loading assigned cities...</p>
            ) : assignedCities.length > 0 ? (
              <table className="assigned-cities-table">
                <thead className="text-white">
                  <tr>
                    <th>S.No</th>
                    <th>City Name</th>
                    <th>State</th>
                   
                  </tr>
                </thead>
                <tbody>
                  {assignedCities.map((city, index) => (
                    <tr key={city.city_id}>
                      <td>{index + 1}</td>
                      <td>{city.city_name}</td>
                      <td>{selectedState}</td>
                  
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No cities assigned to this employee yet.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AssignedCity;
